import { parseKm } from './calc';
import type { Trip, TripCreate } from './api';

/** The «Nuovo viaggio» fields as typed; `km` is one way, as the person measures it. */
export type TripForm = {
  readonly date: string;
  readonly roundTrip: boolean;
  readonly from: string;
  readonly to: string;
  readonly km: string;
  readonly vehicleId: string;
  readonly purpose: string;
  readonly reportId: string;
  readonly saveRoute: boolean;
};

export type TripField = 'date' | 'from' | 'to' | 'km' | 'vehicleId' | 'purpose';

export function emptyTripForm(today: string): TripForm {
  return {
    date: today,
    roundTrip: false,
    from: '',
    to: '',
    km: '',
    vehicleId: '',
    purpose: '',
    reportId: '',
    saveRoute: false,
  };
}

/** The trip read back as a form. The API's km are the claimed ones, so a round trip is halved. */
export function formFromTrip(trip: Trip): TripForm {
  const [whole = '0', fraction = '0'] = trip.km.split('.');
  const tenths = BigInt(whole) * 10n + BigInt(fraction.slice(0, 1) || '0');
  const oneWay = trip.roundTrip ? tenths / 2n : tenths;
  return {
    date: trip.date,
    roundTrip: trip.roundTrip,
    from: trip.from,
    to: trip.to,
    km: `${(oneWay / 10n).toString()}.${(oneWay % 10n).toString()}`,
    vehicleId: trip.vehicle.id,
    purpose: trip.purpose,
    reportId: trip.reportId ?? '',
    saveRoute: false,
  };
}

/** The fields that block a save, in the order the form shows them. */
export function missingTripFields(form: TripForm): ReadonlySet<TripField> {
  const missing = new Set<TripField>();
  if (!/^\d{4}-\d{2}-\d{2}$/.test(form.date)) missing.add('date');
  if (form.from.trim() === '') missing.add('from');
  if (form.to.trim() === '') missing.add('to');
  if (parseKm(form.km) === null) missing.add('km');
  if (form.vehicleId === '') missing.add('vehicleId');
  if (form.purpose.trim() === '') missing.add('purpose');
  return missing;
}

/** The request body; call only when `missingTripFields` is empty. */
export function toTripBody(form: TripForm): TripCreate {
  return {
    date: form.date,
    from: form.from.trim(),
    to: form.to.trim(),
    roundTrip: form.roundTrip,
    km: parseKm(form.km) as string,
    purpose: form.purpose.trim(),
    vehicleId: form.vehicleId,
    ...(form.reportId === '' ? {} : { reportId: form.reportId }),
  };
}
